// Library Methods
import { useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import { ChevronDownIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export type FilterItemType = {
  id: string;
  label: string;
  count: number;
};

export type FilterGroupType = {
  items: Array<FilterItemType>;
  checkedIds: Set<string>;
  onToggleItem: (id: string) => void;
  onToggleAll: (checked: boolean) => void;
};

type FilterSectionType = FilterGroupType & {
  title: string;
};

const FilterSection = ({
  title,
  items,
  checkedIds,
  onToggleItem,
  onToggleAll,
}: FilterSectionType) => {
  const theme = useTheme();
  const { surface, surfaceHover, onSurface, mutedText } =
    getSurfaceTokens(theme);
  const [isExpanded, setIsExpanded] = useState(true);

  // The default surface tokens read as near-black against this modal's
  // lighter dark-mode background (#5C6076), making the filter rows look
  // like separate dark panels -- lighten them here to sit closer to the
  // modal body instead.
  const isDark = theme.palette.mode === "dark";
  const groupBackground = isDark ? "#44485E" : surfaceHover;
  const itemBackground = isDark ? "#35394F" : surface;
  const rowBorderBottom = isDark ? "1px solid #5B6D7A" : "1px solid #5B6D7A30";

  const allChecked = useMemo(
    () => items.length > 0 && items.every((item) => checkedIds.has(item.id)),
    [items, checkedIds]
  );
  const someChecked = useMemo(
    () => items.some((item) => checkedIds.has(item.id)),
    [items, checkedIds]
  );

  return (
    <Box>
      <Box
        role="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "8px",
          backgroundColor: groupBackground,
          color: onSurface,
          borderBottom: rowBorderBottom,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CustomCheckbox
            id={`filter-section-checkbox-${title.replace(/\s+/g, "-").toLowerCase()}`}
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onClick={(e) => {
              e.stopPropagation();
              onToggleAll(!allChecked);
            }}
          />
          <Typography sx={{ fontWeight: 600, fontSize: "12px" }}>
            {title}
          </Typography>
        </Box>

        <ChevronDownIcon
          width="20px"
          height="20px"
          style={{
            color: mutedText,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
          }}
        />
      </Box>

      {isExpanded &&
        items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "8px 8px 8px 32px",
              backgroundColor: itemBackground,
              color: onSurface,
              borderBottom: rowBorderBottom,
              cursor: "pointer",
            }}
            role="button"
            onClick={() => onToggleItem(item.id)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CustomCheckbox
                id={`filter-item-checkbox-${item.id}`}
                checked={checkedIds.has(item.id)}
                checkedFill="#5B6D7A"
                onClick={() => {}}
              />
              <Typography sx={{ fontSize: "12px" }}>{item.label}</Typography>
            </Box>

            <Typography sx={{ fontSize: "12px", color: mutedText }}>
              {item.count}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

export default FilterSection;
